import type { JSONContent } from "@repo/fika-blog/src/tiptap/core"
import { debounce } from "@solid-primitives/scheduled"
import { isDeepEqual } from "remeda"
import {
  createEffect,
  createMemo,
  Match,
  onCleanup,
  Show,
  Switch,
  untrack,
} from "solid-js"
import {
  DraftPostsFragment,
  PostAttachmentFragment,
} from "@/components/post-attachment/post-attachment"
import { Box } from "@/designSystem/components/box"
import { Icon } from "@/designSystem/components/icon"
import { Mini } from "@/designSystem/components/mini/mini"
import { Text } from "@/designSystem/components/text"
import { FoldedNote } from "@/designSystem/patterns/folded-note"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import { defaultJsonContent } from "@/lib/default-json-content"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { ClippedContent } from "./clipped-content"
import {
  noteActionButtonClass,
  noteActionsClass,
  noteContentClass,
  noteFooterClass,
  noteHeaderClass,
  noteHeaderLeftClass,
  noteLabelClass,
} from "./note.css"
import { createNoteEditor, NoteContent, NoteToolbar } from "./note-editor"

export const NoteFragment = graphql(
  `fragment NoteFragment on Note {
    id
    content
    createdAt
    updatedAt
    type
    tags {
      id
      tags
    }
    posts {
      ...PostAttachmentFragment
      ...DraftPostFragment
    }
  }`,
  [PostAttachmentFragment, DraftPostsFragment],
)

interface NoteProps {
  note: FragmentOf<typeof NoteFragment>
  editable?: boolean
  interactive?: boolean
  href?: string
  onClick?: (e: MouseEvent) => void
  showDelete?: boolean
  onDelete?: () => void
  onCopyToPost?: (content: string) => void
  maxContentHeight?: number
}

/**
 * Stores unsaved editor content by note ID, surviving component remounts.
 * When graphcache rolls back optimistic results, SolidJS `For` may remount
 * Note components due to reference changes. This map preserves the user's
 * unsaved edits so the editor can restore them on remount.
 */
const unsavedNoteContent = new Map<string, string>()

export function Note(props: NoteProps) {
  const { LL } = useI18nContext()
  const isEditable = () => props.editable ?? false

  const UpdateNoteMutation = graphql(
    `mutation UpdateNoteContent($id: ID!, $content: String!) {
      updateNote(id: $id, content: $content) {
        ...on MutationUpdateNoteSuccess {
          data {
            id
            content
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )
  const [, updateNote] = createMutationWithErrorHandling(
    UpdateNoteMutation,
    "updateNote",
  )

  const getNote = createFragment(NoteFragment, () => props.note)
  const noteType = createMemo(() => getNote().type ?? "user")

  const noteContent = createMemo<JSONContent>(
    () => {
      try {
        const unsaved = unsavedNoteContent.get(getNote().id)
        const raw = unsaved ?? getNote().content ?? defaultJsonContent
        return JSON.parse(raw)
      } catch {
        return JSON.parse(defaultJsonContent)
      }
    },
    {},
    { equals: isDeepEqual },
  )

  let pendingContent: string | null = null

  function flushSave() {
    const content = pendingContent
    pendingContent = null
    saveContent.clear()
    if (content) {
      updateNote({ id: getNote().id, content }).then(() => {
        unsavedNoteContent.delete(getNote().id)
      })
    }
  }

  const saveContent = debounce(async (content: string) => {
    pendingContent = null
    await updateNote({
      id: getNote().id,
      content,
    })
    unsavedNoteContent.delete(getNote().id)
  }, 1_000)

  const { editor, setEditorRef } = createNoteEditor({
    content: () => untrack(noteContent),
    onUpdate: editorInstance => {
      if (!isEditable()) return
      const content = editorInstance.getJSON()
      if (!content) return

      const contentString = JSON.stringify(content)
      if (contentString !== getNote().content) {
        pendingContent = contentString
        unsavedNoteContent.set(getNote().id, contentString)
        saveContent(contentString)
      }
    },
  })

  createEffect(() => {
    const currentEditor = editor()
    if (currentEditor) {
      currentEditor.setEditable(isEditable())
      if (isEditable()) {
        currentEditor.commands.focus("end")
      } else {
        flushSave()
      }
    }
  })

  onCleanup(() => {
    flushSave()
  })

  function handleDelete(e: MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    props.onDelete?.()
  }

  function handleCopyToPost(e: MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    const editorInstance = editor()
    const content = editorInstance
      ? JSON.stringify(editorInstance.getJSON())
      : getNote().content
    props.onCopyToPost?.(content)
  }

  return (
    <FoldedNote
      hue="emerald"
      interactive={props.interactive ?? true}
      href={props.href}
      onClick={props.onClick}
    >
      <div class={noteHeaderClass}>
        <div class={noteHeaderLeftClass}>
          <Switch>
            <Match when={noteType() === "voice"}>
              <span class={noteLabelClass({ hue: "emerald" })}>
                {LL().notes.voice()}
              </span>
              <Icon name="mic" color="emerald600" size="18" />
            </Match>
          </Switch>
          <Box gap="4" flexDirection="row" alignItems="center">
            <Text color="sand600" fontSize="xs">
              {getNote().updatedAt.toLocaleDateString()}
            </Text>
          </Box>
        </div>
        <Show when={props.onDelete}>
          <div class={noteActionsClass}>
            <button
              type="button"
              onClick={handleDelete}
              class={noteActionButtonClass}
            >
              <Icon name="trash-2" color="sand600" size="18" />
            </button>
          </div>
        </Show>
      </div>
      <Show
        when={isEditable()}
        fallback={
          <Show
            when={props.maxContentHeight}
            fallback={
              <div class={noteContentClass}>
                <NoteContent content={getNote().content || "{}"} />
              </div>
            }
          >
            {maxHeight => (
              <ClippedContent maxHeight={maxHeight()} class={noteContentClass}>
                <NoteContent content={getNote().content || "{}"} />
              </ClippedContent>
            )}
          </Show>
        }
      >
        <Box class={noteContentClass}>
          <Show when={editor()}>{e => <NoteToolbar editor={e()} />}</Show>
          <div ref={setEditorRef} />
        </Box>
      </Show>
      <Show when={props.onCopyToPost}>
        <div class={noteFooterClass}>
          <Mini.Button
            iconName="plus"
            onClick={handleCopyToPost}
            title={LL().notes.copyToPost()}
          />
        </div>
      </Show>
    </FoldedNote>
  )
}
